import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/site/StatusBadge";
import { api, formatApiError } from "@/lib/api";

export function ReviewForm() {
  const [review, setReview] = useState(undefined);
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/me/review").then((r) => { setReview(r.data); if (r.data) { setRating(r.data.rating); setBody(r.data.body); } }).catch(() => setReview(null));
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/me/review", { rating, body });
      setReview(data);
      toast.success("Thank you! Mr. Greg will review it before it goes live.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  if (review === undefined) return null;
  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-[#0B2A4A]/10 p-6 flex flex-col gap-4" data-testid="review-form">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-bold text-[#0B2A4A]">Leave your graduate review</h2>
        {review && <StatusBadge status={review.status} testId="review-status" />}
      </div>
      <div className="flex gap-1" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} type="button" onClick={() => setRating(i)} aria-label={`${i} stars`} data-testid={`review-star-${i}`}>
            <Star className={`h-7 w-7 ${i <= rating ? "fill-[#F08A3C] text-[#F08A3C]" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
      <Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} minLength={10} maxLength={1500} required
        placeholder="What did you build or learn? How did the course help you?" data-testid="review-body-input" />
      <p className="text-xs text-slate-500">Your name, profile photo, rating and review will appear on the public Graduates page after Mr. Greg approves it.</p>
      <button disabled={busy} className="self-start rounded-xl bg-[#F08A3C] text-white px-6 py-3 font-semibold disabled:opacity-60" data-testid="review-submit-btn">{busy ? "Sending..." : review ? "Update review" : "Submit review"}</button>
    </form>
  );
}

export function ReviewsPanel() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/reviews").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const decide = (id, action) => api.post(`/admin/reviews/${id}/${action}`).then(() => { toast.success(action === "approve" ? "Review published" : "Review hidden"); load(); }).catch((e) => toast.error(formatApiError(e)));
  if (!rows) return null;
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="reviews-empty">No graduate reviews yet.</p>;
  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="reviews-panel">
      {rows.map((r) => (
        <div key={r.id} className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-5 flex flex-col gap-3" data-testid="review-row">
          <div className="flex items-start justify-between gap-2">
            <div><p className="font-semibold text-[#0B2A4A]">{r.name}</p><p className="text-xs text-slate-500">{r.email} · {r.course}</p></div>
            <StatusBadge status={r.status} />
          </div>
          <p className="text-sm text-[#F08A3C] font-semibold">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</p>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{r.body}</p>
          <div className="flex gap-2">
            {r.status !== "approved" && <button onClick={() => decide(r.id, "approve")} className="rounded-lg bg-emerald-600 text-white px-4 py-2 text-sm font-semibold" data-testid="review-approve-btn">Approve & publish</button>}
            {r.status !== "hidden" && <button onClick={() => decide(r.id, "hide")} className="rounded-lg border border-[#0B2A4A]/20 px-4 py-2 text-sm font-semibold" data-testid="review-hide-btn">Hide</button>}
          </div>
        </div>
      ))}
    </div>
  );
}
